import { useMutation } from "@tanstack/react-query";
import {
  useCanAccess,
  useDataProvider,
  useEditController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import type { SubmitHandler } from "react-hook-form";
import { SimpleForm } from "@/components/admin/simple-form";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { KeyRound } from "lucide-react";

import type { CrmDataProvider } from "../providers/types";
import type { Sale, SalesFormData } from "../types";
import { SalesInputs } from "./SalesInputs";

function EditToolbar() {
  return (
    <div className="flex flex-wrap justify-end gap-3">
      <CancelButton />
      <SaveButton />
    </div>
  );
}

export function SalesEdit() {
  const { record } = useEditController();

  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const { mutate } = useMutation({
    mutationKey: ["salesUpdate"],
    mutationFn: async (data: SalesFormData) => {
      if (!record) {
        throw new Error(
          translate("resources.sales.edit.record_not_found", {
            _: "Record not found",
          }),
        );
      }
      return dataProvider.salesUpdate(record.id, data);
    },
    onSuccess: () => {
      redirect("/sales");
      notify("resources.sales.edit.success", {
        messageArgs: {
          _: "Team member updated successfully",
        },
      });
    },
    onError: (error: Error) => {
      notify(error.message || "resources.sales.edit.error", {
        type: "error",
        messageArgs: {
          _: "An error occurred. Please try again.",
        },
      });
    },
  });

  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    mutate(data);
  };

  return (
    <div className="max-w-lg w-full mx-auto mt-8">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <SimpleForm
            toolbar={<EditToolbar />}
            onSubmit={onSubmit as SubmitHandler<any>}
            record={record}
          >
            <SaleEditTitle />
            <SalesInputs />
          </SimpleForm>
          <SendResetPasswordButton />
        </CardContent>
      </Card>
    </div>
  );
}

const SaleEditTitle = () => {
  const record = useRecordContext<Sale>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <h2 className="text-lg font-semibold mb-4">
      {translate("resources.sales.edit.title", {
        name: `${record.first_name} ${record.last_name}`,
      })}
    </h2>
  );
};

const SendResetPasswordButton = () => {
  const record = useRecordContext<Sale>();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const translate = useTranslate();

  const { mutate, isPending } = useMutation({
    mutationKey: ["salesResetPassword", record?.id],
    mutationFn: async () => {
      if (!record) throw new Error("Record not found");
      return dataProvider.updatePassword(record.id);
    },
    onSuccess: () => {
      notify("resources.sales.edit.reset_password_success", {
        messageArgs: { _: "Password reset email sent" },
      });
    },
    onError: (error: Error) => {
      notify(
        error.message ||
          translate("resources.sales.edit.reset_password_error", {
            _: "Failed to send password reset email",
          }),
        { type: "error" },
      );
    },
  });

  if (!record || !isAdmin || record.disabled) {
    return null;
  }

  return (
    <div className="border-t pt-4">
      <Button
        type="button"
        variant="outline"
        className="w-full sm:w-auto"
        disabled={isPending}
        onClick={() => mutate()}
      >
        <KeyRound className="size-4" />
        {translate("resources.sales.edit.reset_password", {
          _: "Send password reset",
        })}
      </Button>
      <p className="text-muted-foreground text-xs mt-2">
        Sends a Brevo reset link to {record.email}. They set a new password via
        the same flow as Forgot password.
      </p>
    </div>
  );
};
